import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Check, ChevronRight } from 'lucide-react';

// F2-1: "Set up your club" checklist for club admins, top of the hub.
// Decided in June: soft (nothing is blocked) but persistent: there's no
// dismiss, and it goes by itself once the required steps are done
// (lib/clubSetup.js on the server decides). Established clubs never see it.
const STEP_COPY = {
  details: { label: 'Add your home ground', hint: 'It fills in the venue for training and home games. Add your badge there too', tab: 'settings' },
  team: { label: 'Create your first team', hint: 'Fixtures and training hang off a team', tab: 'teams' },
  invite: { label: 'Invite members and coaches', hint: 'Players, parents, coaches and helpers', tab: 'members' },
  sponsor: { label: 'Add a sponsor', hint: 'Optional', tab: 'sponsors' },
  first_post: { label: 'Post your first fixture or news', hint: 'Optional' },
};

export default function SetupChecklist({ club, clubSlug, onFirstPost }) {
  const { data } = useQuery({
    queryKey: ['setup-status', clubSlug],
    queryFn: () => axios.get(`/clubs/${clubSlug}/setup-status`).then(r => r.data),
    // One cheap SELECT: always re-ask when the hub opens, so a step done in
    // Admin is ticked the moment the admin comes back.
    refetchOnMount: 'always',
  });
  // Loading, an error (e.g. an older server) or done: show nothing.
  if (!data || data.complete || !Array.isArray(data.steps)) return null;
  const pct = Math.round((data.done / data.total) * 100);

  return (
    <section className="hub-setup-checklist" aria-labelledby="hub-setup-title">
      <div className="hub-setup-head">
        <h2 id="hub-setup-title">Set up {club.name}</h2>
        <span className="hub-setup-count">{data.done} of {data.total} done</span>
      </div>
      <div className="hub-setup-bar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="Set-up progress">
        <span style={{ width: `${pct}%` }} />
      </div>
      <ol className="hub-setup-steps">
        {data.steps.map(step => {
          let copy = STEP_COPY[step.key];
          if (!copy) return null;
          // Sponsor tools open once Marakin verifies the club; until then the
          // Sponsors tab is the verification form, so say so.
          if (step.key === 'sponsor' && !club.verified_at) copy = { ...copy, hint: 'Optional. Get verified first, on the Sponsors tab' };
          const body = (
            <>
              <span className={`hub-setup-tick ${step.done ? 'done' : ''}`} aria-hidden="true">
                {step.done && <Check size={14} />}
              </span>
              <span className="hub-setup-text">
                <strong>{copy.label}</strong>
                <span>{step.done ? 'Done' : copy.hint}</span>
              </span>
              {!step.done && <ChevronRight size={18} className="hub-setup-go" aria-hidden="true" />}
            </>
          );
          if (step.done) return <li key={step.key}><div className="hub-setup-step is-done">{body}</div></li>;
          return (
            <li key={step.key}>
              {copy.tab ? (
                <Link className="hub-setup-step" to={`/${clubSlug}/admin?tab=${copy.tab}`}>{body}</Link>
              ) : (
                <button type="button" className="hub-setup-step" onClick={onFirstPost}>{body}</button>
              )}
            </li>
          );
        })}
      </ol>
      <p className="hub-setup-note">This goes away once the first three are done.</p>
    </section>
  );
}
